// ─────────────────────────────────────────────────────────────────────────────
// Every string the viewer renders. English defaults, overridable one key at a
// time, following the Spotlight and sdk-media convention: the catalogues live
// in the apps, so this package owns no copy that has to be translated.
// ─────────────────────────────────────────────────────────────────────────────

export interface FileViewerLabels {
  // Shell
  close: string;
  back: string;
  download: string;
  downloadOriginal: string;
  print: string;
  share: string;
  copyLink: string;
  copyName: string;
  copied: string;
  copyFailed: string;
  openExternally: string;
  refresh: string;
  refreshed: string;
  refreshedNewer: string;
  details: string;
  hideDetails: string;
  fullscreen: string;
  exitFullscreen: string;
  keyboardHelp: string;
  more: string;
  previous: string;
  next: string;
  /** "{index} of {count}" */
  position: string;
  local: string;
  remote: string;
  readOnly: string;
  retry: string;
  cancel: string;
  loadedState: string;
  loadingState: string;
  inspectingState: string;
  processingState: string;
  errorState: string;
  /** "{loaded} of {total}" */
  progressOf: string;
  /** "{loaded} loaded" */
  progressLoaded: string;
  viewerTitle: string;
  untitled: string;

  // Families
  familyPdf: string;
  familyImage: string;
  familyVideo: string;
  familyAudio: string;
  familySpreadsheet: string;
  familyPresentation: string;
  familyDocument: string;
  familyCode: string;
  familyText: string;
  familyArchive: string;
  familyUnknown: string;

  // Details panel
  detailFilename: string;
  detailTitle: string;
  detailType: string;
  detailMime: string;
  detailSize: string;
  detailSource: string;
  detailHost: string;
  detailModified: string;
  detailEtag: string;
  detailChecksum: string;
  computeChecksum: string;
  computingChecksum: string;
  detailDimensions: string;
  detailAspect: string;
  detailDuration: string;
  detailResolution: string;
  detailLines: string;
  detailEncoding: string;
  detailLanguage: string;
  detailValidation: string;
  detailFrames: string;
  detailAlpha: string;
  detailRenderer: string;
  rendererNative: string;
  rendererConverted: string;
  rendererNone: string;
  yes: string;
  no: string;
  unknownValue: string;

  // Errors
  errorNetwork: string;
  errorCors: string;
  /** "{status}" */
  errorHttp: string;
  errorAuth: string;
  errorNotFound: string;
  errorExpired: string;
  /** "{size}" / "{limit}" */
  errorTooLarge: string;
  errorUnsupported: string;
  errorDecode: string;
  errorAborted: string;
  mismatchWarning: string;

  // Unsupported families
  unsupportedTitle: string;
  unsupportedSpreadsheet: string;
  unsupportedPresentation: string;
  unsupportedDocument: string;
  unsupportedArchive: string;
  unsupportedUnknown: string;
  unsupportedPdfInline: string;
  unsupportedMediaCodec: string;
  unsupportedHls: string;
  convertedPreview: string;
  convertedPreviewHint: string;
  loadConvertedPreview: string;
  convertedPreviewFailed: string;
  hexPreview: string;
  hexPreviewHint: string;
  showHexPreview: string;
  originalKept: string;

  // Image
  imageFit: string;
  imageFill: string;
  imageActual: string;
  zoomIn: string;
  zoomOut: string;
  rotateLeft: string;
  rotateRight: string;
  flipHorizontal: string;
  flipVertical: string;
  resetView: string;
  checkerboard: string;
  transformNote: string;
  copyImage: string;
  exportPng: string;
  animatedNote: string;
  svgNote: string;

  // Media
  playbackSpeed: string;
  loop: string;
  pictureInPicture: string;
  fitContain: string;
  fitCover: string;
  fitOriginal: string;
  captureFrame: string;
  quality: string;
  qualityAuto: string;
  live: string;
  bufferHealth: string;
  droppedFrames: string;
  bandwidth: string;
  protocol: string;
  hlsNative: string;
  hlsPlayer: string;
  mediaDiagnostics: string;
  mediaPlaybackFailed: string;
  seekBack: string;
  seekForward: string;
  playPause: string;

  // Text and code
  wrapLines: string;
  lineNumbers: string;
  searchInFile: string;
  /** "{current} of {total}" */
  searchMatches: string;
  searchNone: string;
  goToLine: string;
  language: string;
  viewRaw: string;
  viewRendered: string;
  viewGrid: string;
  prettyPrint: string;
  validJson: string;
  /** "{line}" / "{column}" / "{message}" */
  invalidJson: string;
  copyCode: string;
  /** "{shown}" / "{total}" */
  truncatedNote: string;
  sandboxNote: string;
  noRendererNote: string;
  /** "{rows}" / "{columns}" */
  gridSummary: string;
  emptyText: string;

  // Picker
  pickerTitle: string;
  pickerFamilies: string;
  pickerFromDevice: string;
  pickerDrop: string;
  pickerOr: string;
  pickerChoose: string;
  pickerFromLink: string;
  pickerUrl: string;
  pickerUrlPlaceholder: string;
  pickerOpenLink: string;
  pickerSendCredentials: string;
  pickerSendCredentialsHint: string;
  pickerRemember: string;
  pickerRecentLocal: string;
  pickerRecentLinks: string;
  pickerName: string;
  pickerSize: string;
  pickerModified: string;
  pickerLink: string;
  pickerLastOpened: string;
  pickerLocalNote: string;
  pickerRemoteNote: string;
  /** "{size}" */
  pickerSizeNote: string;
  pickerReopenNote: string;
  pickerInvalidUrl: string;
  pickerNoRecent: string;
  pickerClearRecent: string;

  // Keyboard help
  helpTitle: string;
  helpEscape: string;
  helpArrows: string;
  helpZoom: string;
  helpFit: string;
  helpActual: string;
  helpRotate: string;
  helpSearch: string;
  helpFullscreen: string;
  helpDetails: string;
  helpSpace: string;
  helpSeek: string;
}

export const DEFAULT_LABELS: FileViewerLabels = {
  close: 'Close',
  back: 'Back',
  download: 'Download',
  downloadOriginal: 'Download original',
  print: 'Print',
  share: 'Share',
  copyLink: 'Copy link',
  copyName: 'Copy file name',
  copied: 'Copied',
  copyFailed: 'Could not copy',
  openExternally: 'Open externally',
  refresh: 'Refresh',
  refreshed: 'Reloaded. Nothing changed.',
  refreshedNewer: 'A newer version replaced the one on screen.',
  details: 'Details',
  hideDetails: 'Hide details',
  fullscreen: 'Fullscreen',
  exitFullscreen: 'Exit fullscreen',
  keyboardHelp: 'Keyboard shortcuts',
  more: 'More',
  previous: 'Previous file',
  next: 'Next file',
  position: '{index} of {count}',
  local: 'Local',
  remote: 'Remote',
  readOnly: 'Read only',
  retry: 'Retry',
  cancel: 'Cancel',
  loadedState: 'Loaded',
  loadingState: 'Loading',
  inspectingState: 'Inspecting source',
  processingState: 'Processing',
  errorState: 'Could not open',
  progressOf: '{loaded} of {total}',
  progressLoaded: '{loaded} loaded',
  viewerTitle: 'File viewer',
  untitled: 'Untitled file',

  familyPdf: 'PDF',
  familyImage: 'Image',
  familyVideo: 'Video',
  familyAudio: 'Audio',
  familySpreadsheet: 'Spreadsheet',
  familyPresentation: 'Presentation',
  familyDocument: 'Document',
  familyCode: 'Code',
  familyText: 'Text',
  familyArchive: 'Archive',
  familyUnknown: 'File',

  detailFilename: 'File name',
  detailTitle: 'Title',
  detailType: 'Type',
  detailMime: 'MIME type',
  detailSize: 'Size',
  detailSource: 'Source',
  detailHost: 'Host',
  detailModified: 'Last modified',
  detailEtag: 'ETag',
  detailChecksum: 'Checksum (SHA-256)',
  computeChecksum: 'Compute checksum',
  computingChecksum: 'Computing…',
  detailDimensions: 'Dimensions',
  detailAspect: 'Aspect ratio',
  detailDuration: 'Duration',
  detailResolution: 'Resolution',
  detailLines: 'Lines',
  detailEncoding: 'Encoding',
  detailLanguage: 'Language',
  detailValidation: 'Validation',
  detailFrames: 'Frames',
  detailAlpha: 'Transparency',
  detailRenderer: 'Rendered by',
  rendererNative: 'This browser',
  rendererConverted: 'Converted preview',
  rendererNone: 'Not rendered',
  yes: 'Yes',
  no: 'No',
  unknownValue: 'Unknown',

  errorNetwork: 'The file could not be fetched. Check the connection and try again.',
  errorCors: 'The file’s host does not allow this page to read it. Open it externally or download it instead.',
  errorHttp: 'The server answered {status}.',
  errorAuth: 'You are not allowed to read this file, or the sign-in has expired.',
  errorNotFound: 'The file was not found. It may have been deleted or the link may be wrong.',
  errorExpired: 'This link has expired. Ask for a fresh one.',
  errorTooLarge: 'This file is {size}, above the {limit} this viewer will load. Download it instead.',
  errorUnsupported: 'This browser cannot show this kind of file.',
  errorDecode: 'The file could not be decoded. It may be corrupt.',
  errorAborted: 'Loading was cancelled.',
  mismatchWarning: 'The file’s contents do not match its declared type. It is shown as what the bytes say it is.',

  unsupportedTitle: 'No preview for this file',
  unsupportedSpreadsheet: 'Spreadsheets cannot be drawn by a browser on its own. Download the original to open it in a spreadsheet application.',
  unsupportedPresentation: 'Presentations cannot be drawn by a browser on its own. Download the original to open it in a presentation application.',
  unsupportedDocument: 'Word documents cannot be drawn by a browser on its own. Download the original to open it in a word processor.',
  unsupportedArchive: 'Archives are not opened here. Download it to extract the files.',
  unsupportedUnknown: 'This kind of file has no preview. The original is untouched and can be downloaded.',
  unsupportedPdfInline: 'This browser has no built-in PDF viewer. Download the file or open it externally.',
  unsupportedMediaCodec: 'This browser cannot decode this media. Download it or open it externally.',
  unsupportedHls: 'This browser cannot play HLS streams without a player library, and none is installed here.',
  convertedPreview: 'Converted text preview',
  convertedPreviewHint: 'Text extracted on the server. Layout, images and formatting are not shown; the original is unchanged.',
  loadConvertedPreview: 'Show text preview',
  convertedPreviewFailed: 'The text preview could not be loaded.',
  hexPreview: 'Hex preview',
  hexPreviewHint: 'The first bytes of the file, for telling what it really is.',
  showHexPreview: 'Show hex preview',
  originalKept: 'Nothing here changes the original file.',

  imageFit: 'Fit',
  imageFill: 'Fill',
  imageActual: '1:1',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  rotateLeft: 'Rotate left',
  rotateRight: 'Rotate right',
  flipHorizontal: 'Flip horizontally',
  flipVertical: 'Flip vertically',
  resetView: 'Reset view',
  checkerboard: 'Show transparency grid',
  transformNote: 'View only. Rotating and flipping change nothing in the file.',
  copyImage: 'Copy image',
  exportPng: 'Export preview as PNG',
  animatedNote: 'This format may be animated. The browser plays it as the file dictates.',
  svgNote: 'Shown as a picture. Scripts and external resources inside the SVG never run.',

  playbackSpeed: 'Speed',
  loop: 'Loop',
  pictureInPicture: 'Picture in picture',
  fitContain: 'Fit',
  fitCover: 'Fill',
  fitOriginal: 'Original',
  captureFrame: 'Capture frame',
  quality: 'Quality',
  qualityAuto: 'Auto',
  live: 'Live',
  bufferHealth: 'Buffer',
  droppedFrames: 'Dropped frames',
  bandwidth: 'Bandwidth',
  protocol: 'Protocol',
  hlsNative: 'HLS (built into this browser)',
  hlsPlayer: 'HLS (player library)',
  mediaDiagnostics: 'Diagnostics',
  mediaPlaybackFailed: 'Playback failed. The browser could not decode this file.',
  seekBack: 'Back 10 seconds',
  seekForward: 'Forward 10 seconds',
  playPause: 'Play or pause',

  wrapLines: 'Wrap',
  lineNumbers: 'Line numbers',
  searchInFile: 'Search in file',
  searchMatches: '{current} of {total}',
  searchNone: 'No matches',
  goToLine: 'Go to line',
  language: 'Language',
  viewRaw: 'Raw',
  viewRendered: 'Preview',
  viewGrid: 'Grid',
  prettyPrint: 'Pretty print',
  validJson: 'Valid JSON',
  invalidJson: 'Invalid JSON at line {line}, column {column}: {message}',
  copyCode: 'Copy contents',
  truncatedNote: 'Showing the first {shown} of {total}. Download the file for the rest.',
  sandboxNote: 'Rendered in a sandbox: no scripts, forms or outside requests.',
  noRendererNote: 'No renderer is installed for this format, so it is shown as text.',
  gridSummary: '{rows} rows, {columns} columns',
  emptyText: 'This file is empty.',

  pickerTitle: 'Open a file',
  pickerFamilies: 'Supported file families',
  pickerFromDevice: 'Open from device',
  pickerDrop: 'Drop a file here',
  pickerOr: 'or',
  pickerChoose: 'Choose from device',
  pickerFromLink: 'Open from link',
  pickerUrl: 'File URL',
  pickerUrlPlaceholder: 'https://',
  pickerOpenLink: 'Open link',
  pickerSendCredentials: 'Send credentials',
  pickerSendCredentialsHint: 'Include this site’s cookies with the request. Only for links on a host that expects them.',
  pickerRemember: 'Remember this link',
  pickerRecentLocal: 'Recent local files',
  pickerRecentLinks: 'Recent remote links',
  pickerName: 'Name',
  pickerSize: 'Size',
  pickerModified: 'Modified',
  pickerLink: 'Link',
  pickerLastOpened: 'Last opened',
  pickerLocalNote: 'Local files stay on this device unless you share them.',
  pickerRemoteNote: 'The host of a link may refuse this page, or ask for a sign-in.',
  pickerSizeNote: 'Files up to {size} open here. For larger files, use a link.',
  pickerReopenNote: 'A browser cannot reopen a device file by itself. Choose it again to open it.',
  pickerInvalidUrl: 'Enter a full https:// link.',
  pickerNoRecent: 'Nothing yet.',
  pickerClearRecent: 'Clear',

  helpTitle: 'Keyboard shortcuts',
  helpEscape: 'Close, or leave fullscreen',
  helpArrows: 'Previous or next file',
  helpZoom: 'Zoom in or out',
  helpFit: 'Fit to view',
  helpActual: 'Actual size',
  helpRotate: 'Rotate clockwise',
  helpSearch: 'Search',
  helpFullscreen: 'Fullscreen',
  helpDetails: 'Show or hide details',
  helpSpace: 'Play or pause',
  helpSeek: 'Seek back or forward'
};

/** `{name}` substitution, for the handful of labels that carry a value. */
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}
